import type { PermissionKey } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, EmptyState, Icon, Skeleton } from "@sagot/ui";
import type { ReactNode } from "react";
import { Outlet } from "react-router";
import { usePermissions, useRouteAgentId } from "../session/permissions";
import { useWorkspaceContext } from "../session/workspace-context";

function HomeButton() {
  const { t } = useTranslation();
  return (
    <Button asChild variant="secondary">
      <AppLink href="/">
        <Icon name="arrow-left" />
        {t("console.goHome")}
      </AppLink>
    </Button>
  );
}

/** 403 inside the shell: the server's permissions don't include this page. */
export function ForbiddenPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="mx-auto max-w-3xl">
      <div className="card">
        <EmptyState
          icon="lock"
          headingLevel={2}
          title={t("console.forbiddenTitle")}
          description={t("console.forbiddenBody", { workspace: summary.name })}
          action={<HomeButton />}
        />
      </div>
      <h1 className="sr-only">{t("console.forbiddenTitle")}</h1>
    </div>
  );
}

/** Full-page state for a slug the user is not a member of. */
export function NoWorkspacePage() {
  const { t } = useTranslation();
  return (
    <main className="flex min-h-screen items-center justify-center bg-canvas px-4">
      <div className="card w-full max-w-lg">
        <h1 className="sr-only">{t("console.noWorkspaceTitle")}</h1>
        <EmptyState icon="building" headingLevel={2} title={t("console.noWorkspaceTitle")} description={t("console.noWorkspaceBody")} action={<HomeButton />} />
      </div>
    </main>
  );
}

interface RequirePermissionProps {
  permission: PermissionKey;
  /** Workspace pages that filter rows by scope pass `anywhere`; agent pages check the route agent. */
  mode?: "workspace" | "anywhere" | "agent";
  children?: ReactNode;
}

/** Route-level gate: renders the 403 page unless the server grants the view permission. */
export function RequirePermission({ permission, mode = "anywhere", children }: RequirePermissionProps) {
  const routeAgentId = useRouteAgentId();
  const agentId = mode === "agent" ? routeAgentId : null;
  const permissions = usePermissions(agentId);
  if (permissions.isPending) {
    return (
      <div className="mx-auto max-w-7xl space-y-4" aria-hidden="true">
        <Skeleton className="h-8 w-[220px]" />
        <Skeleton className="h-4 w-[360px] max-w-full" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  const allowed = permissions.can(permission, mode === "agent" ? { agentId } : { anywhere: mode === "anywhere" });
  if (!allowed) return <ForbiddenPage />;
  return children ?? <Outlet />;
}
